import i18n, { Formatter } from 'i18next'
import BrowserLngDetector from 'i18next-browser-languagedetector'
import { initReactI18next } from 'react-i18next'
import HttpBackend, { HttpBackendOptions } from 'i18next-http-backend'
import { localeFallback } from '@migrate/utils/common'
import { zodI18nMap } from 'zod-i18n-map'
import { z } from 'zod'
import { LocalDate, ZonedDateTime } from '@js-joda/core'
import { fromLocalDate, fromZonedDateTime } from '@migrate/utils/date'
import intervalPlural from 'i18next-intervalplural-postprocessor'

export const ns = [
  'translation',
  'common',
  'admin',
  'admin-console',
  'auth',
  'dashboard',
  'ingestion',
  'navigator',
  'stages',
  'preferences',
  'zod',
]
export const defaultNS = 'common'

// Must stay a *mutable* array of string literals: i18next's `ParseKeysByFallbackNs` only
// matches `(infer U extends string)[]`, so a `readonly` tuple (i.e. `as const`) silently
// widens `ParseKeys` to accept any string, and a plain `string[]` does the same.
export type FallbackNs = 'zod' | 'common' | 'translation'
export const fallbackNS: FallbackNs[] = ['zod', 'common', 'translation']

type FormatterParams = Parameters<Formatter['add']>

const formatters: { [name: FormatterParams[0]]: FormatterParams[1] } = {
  monogram: (value, lng) => {
    if (lng && lng.split('-')[0] === 'ja') {
      // TODO: Detect kanji, etc and do the right thing
      return value
    }
    return value
      .split(' ')
      .filter((part: string) => part[0])
      .filter((_ignored: string, index: number) => index < 2)
      .map((part: string) => part[0].toUpperCase())
      .join('')
  },
  array_to_comma_string: (value) => {
    if (!Array.isArray(value)) {
      return value
    }
    return value.join(', ')
  },
  local_date: (value, lng, { formatParams }) => {
    if (!(value instanceof LocalDate)) {
      console.error('Invalid date passed to local_date formatter', value)
      return value
    }

    return fromLocalDate(value).toLocalizedString(lng, formatParams)
  },
  zoned_date_time: (
    value,
    lng,
    {
      formatParams = {
        year: 'numeric',
        month: 'numeric',
        day: 'numeric',
        hour: 'numeric',
        minute: 'numeric',
      },
    },
  ) => {
    if (!(value instanceof ZonedDateTime)) {
      console.error('Invalid date passed to zoned_date_time formatter', value)
      return value
    }

    const date = fromZonedDateTime(value).toNative()
    return Intl.DateTimeFormat(lng, formatParams).format(date)
  },
}

export const initI18next = async () => {
  // Keeps <html lang> on the active language, for screen readers and the browser's own features.
  i18n.on('languageChanged', (lng) => {
    document.documentElement.lang = lng
  })

  await i18n
    .use(intervalPlural)
    .use(BrowserLngDetector)
    .use(HttpBackend)
    .use(initReactI18next)
    .init<HttpBackendOptions>({
      ns,
      defaultNS,
      fallbackNS,
      backend: {
        loadPath: `/locale/{{lng}}/{{ns}}.json`,
      },
      fallbackLng: localeFallback,
      interpolation: {
        escapeValue: false,
      },
      detection: {
        order: ['cookie'],
        caches: ['cookie'],
        lookupCookie: 'i18next',
      },
      react: {
        bindI18n: 'loaded languageChanged',
        bindI18nStore: 'added',
        useSuspense: true,
      },
    })

  Object.keys(formatters).forEach((name) => {
    i18n.services.formatter?.add(name, formatters[name])
  })

  z.setErrorMap(zodI18nMap)
}
